<template>
  <div>
    <section v-if="customerProfileEntryMode" class="profile-entry-page">
      <div class="profile-entry-canvas">
        <div class="profile-entry-copy">
          <p class="entry-eyebrow">Customer Portal</p>
          <h1>Complete your profile before entering the dashboard</h1>
          <p>
            The customer profile form is required for first-time portal access. You can close it and continue to the dashboard, but submission
            creation will stay locked until the profile is completed.
          </p>
        </div>
      </div>
    </section>

    <AppShell v-else>
      <template #sidebar>
        <SidebarNav
          :user-name="displayName"
          :active-key="activeTab"
          :items="navItems"
          role="User"
          @select="onSidebarSelect"
        />
      </template>
      <template #header>
        <HeaderBar />
      </template>

      <div class="panel">
        <HomePanel
          v-if="activeTab === 'home'"
          :show-profile-warning="showCustomerProfileWarning"
          :create-disabled="createSubmissionDisabled"
          @complete-profile="openCustomerProfileDialog"
          @create="goToSubmit"
        />
        <RequestsList
          v-else-if="activeTab === 'active'"
          mode="active"
          :current-user="currentUser"
          @countsChanged="onCounts"
        />
        <RequestsList
          v-else-if="activeTab === 'past'"
          mode="past"
          :current-user="currentUser"
          @countsChanged="onCounts"
        />
        <CustomerProfilePanel
          v-else-if="activeTab === 'profile'"
          :profile="customerProfile"
          :profile-completed="customerProfileComplete"
          :user-email="currentUser?.email || ''"
          @edit-profile="openCustomerProfileDialog"
        />
      </div>
    </AppShell>

    <CustomerProfileDialog
      :model-value="showCustomerProfileDialog"
      :initial-profile="customerProfile"
      :current-user="currentUser"
      :saving="customerProfileSaving"
      @close="handleCustomerProfileClose"
      @save="saveCustomerProfile"
    />

    <teleport to="body">
      <div
        v-if="showForm"
        class="modal-overlay-teleport"
        @click="closeForm"
      >
        <MultiStepForm
          embedded
          @form-completed="handleFormCompleted"
          @close="closeForm"
          @click.stop
        />
      </div>
    </teleport>
  </div>
</template>

<script>
import { Collection, Document, House, User } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { signOut } from 'firebase/auth'
import { auth, serverTimestamp } from '@/firebase'
import AppShell from '@/layouts/AppShell.vue'
import SidebarNav from '@/components/SidebarNav.vue'
import HeaderBar from '@/components/HeaderBar.vue'
import HomePanel from '@/components/HomePanel.vue'
import RequestsList from '@/components/RequestsList.vue'
import CustomerProfileDialog from '@/components/CustomerProfileDialog.vue'
import CustomerProfilePanel from '@/components/CustomerProfilePanel.vue'
import MultiStepForm from '@/components/MultiStepForm.vue'
import { CASE_SCOPES, watchCount } from '@/services/requests'
import {
  getCustomerProfile,
  isCustomerProfileComplete,
  mergeUserProfile,
  resolveUserProfile,
} from '@/services/userProfile'

export default {
  name: 'CustomerDashboard',
  components: {
    AppShell,
    SidebarNav,
    HeaderBar,
    HomePanel,
    RequestsList,
    CustomerProfileDialog,
    CustomerProfilePanel,
    MultiStepForm,
  },
  props: {
    currentUser: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      activeTab: 'home',
      activeCount: 0,
      pastCount: 0,
      displayName: '',
      customerProfile: {},
      customerProfileComplete: false,
      showCustomerProfileDialog: false,
      customerProfileSaving: false,
      showForm: false,
      unsubs: { active: null, past: null },
    }
  },
  computed: {
    navItems() {
      return [
        { key: 'home', label: 'Home', icon: House },
        { key: 'active', label: 'Active Requests', icon: Document, badge: this.activeCount },
        { key: 'past', label: 'Past Requests', icon: Collection, badge: this.pastCount },
        { key: 'profile', label: 'Profile', icon: User },
      ]
    },
    customerProfileEntryRequested() {
      return this.$route.query?.customerProfile === '1'
    },
    customerProfileEntryMode() {
      return this.customerProfileEntryRequested && !this.customerProfileComplete
    },
    createSubmissionDisabled() {
      return !this.customerProfileComplete
    },
    showCustomerProfileWarning() {
      return this.activeTab === 'home'
        && !this.customerProfileComplete
        && !this.showCustomerProfileDialog
        && !this.customerProfileEntryMode
    },
  },
  async mounted() {
    await this.loadProfileState()
    if (this.customerProfileEntryMode) this.showCustomerProfileDialog = true
    this.subscribeCounts()
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.onEsc)
    this.unsubs.active && this.unsubs.active()
    this.unsubs.past && this.unsubs.past()
  },
  methods: {
    async loadProfileState() {
      const user = this.currentUser || auth.currentUser
      const profile = await resolveUserProfile(user).catch(() => null)
      this.customerProfile = getCustomerProfile(profile)
      this.customerProfileComplete = isCustomerProfileComplete(profile)
      this.displayName = profile?.data?.name || user?.displayName || user?.email || 'User'
    },
    subscribeCounts() {
      const uid = this.currentUser?.uid || auth.currentUser?.uid
      if (!uid) return

      this.unsubs.active && this.unsubs.active()
      this.unsubs.past && this.unsubs.past()

      this.unsubs.active = watchCount({ scope: CASE_SCOPES.CUSTOMER_ACTIVE, uid }, (count) => {
        this.activeCount = count
      })
      this.unsubs.past = watchCount({ scope: CASE_SCOPES.CUSTOMER_PAST, uid }, (count) => {
        this.pastCount = count
      })
    },
    onSidebarSelect(key) {
      if (key === 'logout') {
        this.logout()
        return
      }
      this.activeTab = key
    },
    onCounts(payload) {
      if (payload.active !== undefined) this.activeCount = payload.active
      if (payload.past !== undefined) this.pastCount = payload.past
    },
    clearCustomerProfileQuery() {
      if (!this.$route.query?.customerProfile) return
      const nextQuery = { ...this.$route.query }
      delete nextQuery.customerProfile
      this.$router.replace({ query: nextQuery }).catch(() => {})
    },
    openCustomerProfileDialog() {
      this.showCustomerProfileDialog = true
    },
    handleCustomerProfileClose() {
      this.showCustomerProfileDialog = false
      this.clearCustomerProfileQuery()
    },
    async saveCustomerProfile(profileData) {
      if (this.customerProfileSaving) return
      const user = this.currentUser || auth.currentUser
      if (!user) {
        this.$router.push({ name: 'Login' })
        return
      }

      this.customerProfileSaving = true
      try {
        await mergeUserProfile(user, {
          profile: {
            ...profileData,
            completed: true,
            completedAt: this.customerProfile.completedAt || serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
        })

        await this.loadProfileState()
        this.showCustomerProfileDialog = false
        this.clearCustomerProfileQuery()
        ElMessage({
          type: 'success',
          message: 'Your customer profile has been saved.',
          showClose: true,
        })
      } catch (error) {
        ElMessage({
          type: 'error',
          message: error?.message || 'Unable to save your customer profile right now.',
          showClose: true,
          duration: 5000,
        })
      } finally {
        this.customerProfileSaving = false
      }
    },
    async logout() {
      try {
        await signOut(auth)
      } catch (error) {}
      this.$router.push({ name: 'Login' })
    },
    goToSubmit() {
      if (!auth.currentUser) {
        this.$router.push({ name: 'Login' })
        return
      }
      if (this.createSubmissionDisabled) {
        ElMessage({
          type: 'warning',
          message: 'Complete your customer profile before creating a submission.',
          showClose: true,
        })
        this.openCustomerProfileDialog()
        return
      }
      this.showForm = true
      document.body.style.overflow = 'hidden'
      window.addEventListener('keydown', this.onEsc)
    },
    closeForm() {
      this.showForm = false
      document.body.style.overflow = ''
      window.removeEventListener('keydown', this.onEsc)
    },
    handleFormCompleted() {
      this.closeForm()
      ElMessage({
        type: 'success',
        message: 'Your request has been successfully created.',
        showClose: true,
      })
    },
    onEsc(event) {
      if (event.key === 'Escape' || event.key === 'Esc') this.closeForm()
    },
  },
}
</script>

<style scoped>
.profile-entry-page {
  min-height: 100vh;
  background:
    radial-gradient(circle at top, rgba(11, 108, 255, 0.12), transparent 35%),
    linear-gradient(180deg, #f7fbff 0%, #eef5ff 100%);
}

.profile-entry-canvas {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
}

.profile-entry-copy {
  max-width: 720px;
  text-align: center;
  color: #03241f;
}

.entry-eyebrow {
  margin: 0 0 10px;
  color: #0b6cff;
  font-size: 0.85rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.profile-entry-copy h1 {
  margin: 0 0 12px;
  font-size: 2.4rem;
  font-weight: 850;
}

.profile-entry-copy p:last-child {
  margin: 0;
  font-size: 1.05rem;
  color: #4f6d67;
}

.panel {
  margin-top: 80px;
  box-sizing: border-box;
}

.modal-overlay-teleport {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(2, 8, 6, 0.56);
  padding: 28px;
  z-index: 2147483647;
}

@media (max-width: 880px) {
  .profile-entry-copy h1 {
    font-size: 1.85rem;
  }
}
</style>
